import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtDateTime, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { roleLabel } from "@/lib/role";
import AdminLayout from "@/layouts/AdminLayout";
import { amdenNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserCheck, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";

const BLANK = { name: "", email: "", password: "", role: "kqm_admin" };

export default function KqmUsers() {
  const { impersonate } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [lockedMap, setLockedMap] = useState({});

  const load = () => api.get("/users").then((r) => setRows(r.data));
  const loadLocked = () => api.get("/admin/locked-logins").then((r) => {
    const m = {}; (r.data.locked || []).forEach((l) => { m[l.email] = l.minutes_remaining; }); setLockedMap(m);
  }).catch(() => {});
  useEffect(() => { load(); loadLocked(); }, []);
  const unlockUser = async (email) => {
    try { await api.post("/admin/unlock-login", { email }); toast.success("Login unlocked"); loadLocked(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openCreate = () => { setEditId(null); setForm(BLANK); setDialog(true); };
  const openEdit = (u) => { setEditId(u.id); setForm({ name: u.name || "", email: u.email || "", password: "", role: u.role }); setDialog(true); };

  const doImpersonate = async (id) => {
    try { await impersonate(id); toast.success("Support mode active"); const u = rows.find((x) => x.id === id); nav(u.role === "employee" ? "/app" : u.role === "kqm_manager" ? "/manager" : "/admin"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const save = async () => {
    try {
      if (editId) { const payload = { name: form.name, email: form.email, role: form.role }; if (form.password) payload.password = form.password; await api.put(`/users/${editId}`, payload); toast.success("User updated"); }
      else { await api.post("/users", form); toast.success("User created"); }
      setDialog(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (u) => { await api.put(`/users/${u.id}/${u.active ? "deactivate" : "activate"}`); load(); };

  const filtered = rows.filter((u) => `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <AdminLayout nav={amdenNav} title="KQM Users">
      <PageHeader title="KQM Users" subtitle="Edit accounts, reset passwords & impersonate for support">
        <Button data-testid="new-user-btn" onClick={openCreate} className="bg-slate-900 text-white">+ New user</Button>
      </PageHeader>
      <Input data-testid="user-search" placeholder="Search users…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm mb-4" />
      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto kqm-scroll">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs font-semibold uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
            <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Last login</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-zinc-50 hover:bg-zinc-50">
                <td className="p-3 font-medium">{u.name}</td>
                <td className="p-3 text-zinc-500">{u.email}</td>
                <td className="p-3"><span className="text-xs font-semibold bg-zinc-100 px-2 py-0.5 rounded">{roleLabel(u.role)}</span></td>
                <td className="p-3 num text-xs">{u.last_login ? fmtDateTime(u.last_login) : "—"}</td>
                <td className="p-3"><StatusBadge status={u.active ? "active" : "archived"} />{lockedMap[u.email] != null && <span className="ml-1 text-[10px] font-bold uppercase bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded" data-testid={`user-locked-${u.id}`}>Locked</span>}</td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    {lockedMap[u.email] != null && <Button data-testid={`user-unlock-${u.id}`} size="sm" variant="outline" className="h-8 border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => unlockUser(u.email)}><Lock className="h-3.5 w-3.5 mr-1" />Unlock</Button>}
                    <Button data-testid={`user-edit-${u.id}`} size="sm" variant="outline" className="h-8" onClick={() => openEdit(u)}><Pencil className="h-3.5 w-3.5" /></Button>
                    {u.role !== "amden_super" && <Button data-testid={`impersonate-${u.id}`} size="sm" className="h-8 bg-amber-500 text-black font-bold" onClick={() => doImpersonate(u.id)}><UserCheck className="h-3.5 w-3.5 mr-1" />Impersonate</Button>}
                    <Button data-testid={`toggle-${u.id}`} size="sm" variant="outline" className="h-8" onClick={() => toggle(u)}>{u.active ? "Disable" : "Enable"}</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editId ? "Edit user" : "New user"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="u-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Email</Label><Input data-testid="u-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div><Label>{editId ? "New password (leave blank to keep)" : "Password"}</Label><Input data-testid="u-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="u-role" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="kqm_admin">Client Admin</SelectItem>
                  <SelectItem value="amden_super">Amden Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="u-submit" onClick={save} className="w-full bg-slate-900 text-white">{editId ? "Save changes" : "Create user"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
